import styled, { css, keyframes } from "styled-components";

/*
 * Bend detalj, v2 redizajn (s.4.2): hero cover 40vh, bio + članovi,
 * merch grid (jezgro stranice), novosti benda.
 *
 * Animacije: hero na mount (cover settle + ime + meta + social stagger),
 * sekcije na scroll (useReveal → $visible), grid kartice per-ćelija.
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

const coverIn = keyframes`
  from { opacity: 0.4; transform: scale(1.06); }
  to { opacity: 1; transform: scale(1); }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.6); }
  to { opacity: 1; transform: scale(1); }
`;

/* Reveal na scroll, bezbedan default: bez prosleđenog $visible elemenat je
   vidljiv; sakriven je samo dok je $visible === false */
const revealSafe = css`
  opacity: ${(p) => (p.$visible === false ? 0 : 1)};
  transform: translateY(${(p) => (p.$visible === false ? "26px" : "0")});
  transition:
    opacity 0.6s ease,
    transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

export const Hero = styled.section`
  position: relative;
  min-height: 40vh;
  display: flex;
  align-items: flex-end;
  background: var(--bg-elevated);
  overflow: hidden;

  img.cover {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    animation: ${coverIn} 1.1s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }

  @media (prefers-reduced-motion: reduce) {
    img.cover {
      animation: none;
    }
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(19, 16, 16, 0.25) 0%,
      transparent 35%,
      rgba(19, 16, 16, 0.92) 100%
    );
  }
`;

export const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 3.5rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const HeroName = styled.h1`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(40px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.1s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HeroMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  color: var(--text-secondary);
  font-size: 0.92rem;
  animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) 0.25s both;

  .dot {
    color: var(--text-muted);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;

  /* social ikonice iskaču jedna za drugom */
  & > * {
    animation: ${popIn} 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  }

  ${Array.from(
    { length: 10 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${380 + i * 50}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

export const SocialButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: rgba(19, 16, 16, 0.55);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    color: var(--brand);
    border-color: var(--brand);
  }
`;

/* --------------------------------- telo --------------------------------- */
export const Page = styled.div`
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem 5rem;
`;

export const Section = styled.section`
  margin-top: 3.5rem;
`;

export const SectionLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
  ${revealSafe}
`;

export const SectionH2 = styled.h2`
  margin: 0 0 1.5rem;
  display: flex;
  align-items: baseline;
  gap: 0.9rem;
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);

  small {
    font-family: var(--font-body);
    font-size: 0.95rem;
    font-weight: 400;
    color: var(--text-secondary);
    text-transform: none;
    letter-spacing: 0;
  }

  ${revealSafe}
`;

export const Prose = styled.div`
  max-width: 720px;
  color: var(--text-secondary);
  font-size: 0.98rem;
  line-height: 1.75;
  ${revealSafe}

  p {
    margin: 0 0 1rem;
  }
`;

/* Biografija skraćena na 3 reda; puna visina se prosleđuje inline max-height
   stilom pri otvaranju da bi tranzicija imala tačnu ciljnu vrednost */
export const BioClamp = styled.div`
  position: relative;
  overflow: hidden;
  max-height: ${(p) => (p.$collapsed ? "calc(3 * 1.75em)" : "none")};
  transition: max-height 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);

  p:last-child {
    margin-bottom: 0;
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3.2em;
    background: linear-gradient(to bottom, transparent, var(--bg-app));
    opacity: ${(p) => (p.$fade ? 1 : 0)};
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

export const BioToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.75rem;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.18s ease;

  svg {
    transition: transform 0.25s ease;
    transform: rotate(${(p) => (p.$expanded ? "180deg" : "0deg")});
  }

  &:hover {
    color: var(--brand);
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

export const MembersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.25rem;

  /* chipovi članova ulete jedan za drugim kad sekcija postane vidljiva */
  & > * {
    opacity: ${(p) => (p.$visible === false ? 0 : 1)};
  }

  ${(p) =>
    p.$visible &&
    css`
      & > * {
        animation: ${fadeUp} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      }

      ${Array.from(
        { length: 10 },
        (_, i) => css`
          & > *:nth-child(${i + 1}) {
            animation-delay: ${100 + i * 50}ms;
          }
        `,
      )}
    `}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      opacity: 1;
      animation: none;
    }
  }
`;

export const MemberChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 0.9rem;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  color: var(--text-primary);
  font-size: 0.85rem;
  font-weight: 500;

  small {
    color: var(--text-muted);
    font-size: 0.75rem;
  }
`;

export const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

export const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/* Ćelija grida sa scroll-reveal, wrapper oko deljenih kartica
   (ProductCard/NewsCard); flex rasteže karticu na punu visinu reda */
export const RevealCell = styled.div`
  display: flex;
  flex-direction: column;

  & > * {
    flex: 1;
  }

  opacity: 0;

  ${(p) =>
    p.$visible &&
    css`
      opacity: 1;
      animation: ${fadeUp} 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      animation-delay: calc(var(--i, 0) * 60ms);
    `}

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

export const SectionFooter = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.75rem;
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 46px;
  padding: 0 1.5rem;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  svg {
    transition: transform 0.18s ease;
  }

  &:hover {
    border-color: var(--brand);
    color: var(--brand);

    svg {
      transform: translateX(3px);
    }
  }
`;

export const EmptyNote = styled.p`
  color: var(--text-muted);
  font-size: 0.92rem;
`;

export const StateContainer = styled.div`
  padding: 6rem 1rem;
  text-align: center;
  color: var(--text-secondary);
`;
